import React from 'react';
import { Row, Col, Spin, Typography } from 'antd';
import {
  ShopOutlined,
  SettingOutlined,
  RocketOutlined,
  LineChartOutlined,
  AimOutlined,
} from '@ant-design/icons';
import { DashboardSectionCard } from '../../components/Dashboard/DashboardSectionCard';
import type { ProductDashboardSummary } from '../../services/bridgeSessionDataAdapter';
import styles from './ProductDashboard.module.css';

const { Title, Text } = Typography;

type Subdomain = 'operations' | 'development' | 'projections' | 'marketfit';

export type ProductDashboardSummarySectionProps = {
  productName: string;
  summaryLoading: boolean;
  summary: ProductDashboardSummary | null;
  selectedSubdomain: Subdomain;
  onOperations: () => void;
  onDevelopment: () => void;
  onProjections: () => void;
  onMarketFit: () => void;
};

export function ProductDashboardSummarySection({
  productName,
  summaryLoading,
  summary,
  selectedSubdomain,
  onOperations,
  onDevelopment,
  onProjections,
  onMarketFit,
}: ProductDashboardSummarySectionProps) {
  const opsMetrics = summary?.operations ?? { demand: '—', supply: '—', delivery: '—' };
  const devMetrics = summary?.development ?? { releases: '—', inProgress: '—', planned: '—' };
  const projMetrics = summary?.projections ?? { revenue: '—', cogs: '—', margin: '—' };
  const mfMetrics = summary?.marketFit ?? { deals: '—', avgScore: '—', active: '—' };

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <ShopOutlined className={styles.headerIcon} />
          <div>
            <Title level={2} className={styles.title}>
              {productName}
            </Title>
            <Text className={styles.subtitle}>
              Product operations, development, projections, and market fit
            </Text>
          </div>
        </div>
      </div>

      <div className={styles.summaryRow}>
        {summaryLoading ? (
          <div className={styles.summaryLoading}>
            <Spin size="small" />
            <span>Loading metrics...</span>
          </div>
        ) : (
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} lg={6}>
              <DashboardSectionCard
                title="Operations"
                icon={<SettingOutlined />}
                description="Demand, supply, and delivery"
                onClick={onOperations}
                className={selectedSubdomain === 'operations' ? styles.selected : ''}
                metrics={[
                  { label: 'Demand', value: opsMetrics.demand },
                  { label: 'Supply', value: opsMetrics.supply },
                  { label: 'Delivery', value: opsMetrics.delivery },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <DashboardSectionCard
                title="Development"
                icon={<RocketOutlined />}
                description="Objectives and release timeline"
                onClick={onDevelopment}
                className={selectedSubdomain === 'development' ? styles.selected : ''}
                metrics={[
                  { label: 'Releases', value: devMetrics.releases },
                  { label: 'In Progress', value: devMetrics.inProgress },
                  { label: 'Planned', value: devMetrics.planned },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <DashboardSectionCard
                title="Projections"
                icon={<LineChartOutlined />}
                description="Revenue and expense forecasts"
                onClick={onProjections}
                className={selectedSubdomain === 'projections' ? styles.selected : ''}
                metrics={[
                  { label: 'Revenue', value: projMetrics.revenue },
                  { label: 'COGS', value: projMetrics.cogs },
                  { label: 'Margin', value: projMetrics.margin },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <DashboardSectionCard
                title="MarketFit"
                icon={<AimOutlined />}
                description="Deals, situations, and solutions"
                onClick={onMarketFit}
                className={selectedSubdomain === 'marketfit' ? styles.selected : ''}
                metrics={[
                  { label: 'Deals', value: mfMetrics.deals },
                  { label: 'Avg Score', value: mfMetrics.avgScore },
                  { label: 'Active', value: mfMetrics.active },
                ]}
              />
            </Col>
          </Row>
        )}
      </div>
    </>
  );
}
